import {
  activityHistoryFromPath,
  activityHistoryPage,
} from '@components/ActivityHistory/routes'
import { AssistantPart } from '@services/modules/AssistantStream'
import { observer } from 'mobx-react'
import { ComponentType } from 'react'

import { openAssistantRecord, openAssistantRoute } from '../assistantNavigation'
import type { AssistantPageContext } from '../assistantPageContext'
import { useAssistantSession } from '../AssistantSessionContext'
import { useAssistantPlacement } from '../placements/PlacementContext'
import { HandoffLink, MetaRow } from '../ui'

import ConfirmationPart from './ConfirmationPart'

/*
 * Renderers for the structured parts the server attaches to an answer. A new part type is one
 * component plus one registry entry; unknown types render nothing, so an older client stays
 * safe when the server learns a new part.
 */

interface PartProps {
  part: AssistantPart
}

/**
 * A hand-off to a record's History page ("Open case history") opens the page's own tab, the one
 * the History modal opens too, named after the case when it is the case on screen.
 */
const openHistoryPage = (
  subjectType: string,
  id: string,
  /** The record the panel's questions are about (its session's activePageContext). */
  context: AssistantPageContext | null
) => {
  const sameCase =
    !!context &&
    !context.list &&
    context.type === 'case' &&
    context.id === id.toLowerCase()
  openAssistantRecord(
    activityHistoryPage(subjectType, id, sameCase ? context?.label : undefined)
  )
}

const openHandoff = (
  part: AssistantPart,
  context: AssistantPageContext | null
) => {
  const history = activityHistoryFromPath(part.route)
  if (history && Object.keys(part.query ?? {}).length === 0) {
    openHistoryPage(history.subjectType, history.id, context)
    return
  }
  openAssistantRoute(part.route, part.query)
}

/** "Open in list view": the classic screen pre-filtered with what the assistant searched. */
const HandoffPart = ({ part }: PartProps) => {
  const session = useAssistantSession()
  if (!part.route) {
    return null
  }
  return (
    <HandoffLink onClick={() => openHandoff(part, session.activePageContext)}>
      {part.label || 'Open in list view'}
    </HandoffLink>
  )
}

/** "Open the … guide": the page of the user guide the answer came from. */
const GuidePart = ({ part }: PartProps) => {
  const session = useAssistantSession()
  if (!part.docKey) {
    return null
  }
  return (
    <HandoffLink onClick={() => session.openGuide(part.docKey)}>
      {part.label || 'Open the guide'}
    </HandoffLink>
  )
}

/** Inline parts share one footer row; block parts (cards) take the full width. */
const INLINE_PARTS: Record<string, ComponentType<PartProps>> = {
  handoff: HandoffPart,
  guide: GuidePart,
}

const BLOCK_PARTS: Record<string, ComponentType<PartProps>> = {
  confirmation: ConfirmationPart,
}

const MessageParts = ({ parts }: { parts?: AssistantPart[] }) => {
  const session = useAssistantSession()
  const { hidesLink, header } = useAssistantPlacement()
  const blocks = (parts ?? []).filter(p => BLOCK_PARTS[p.type])
  // Two searches of one answer can hand off to the same list with the same filters: one link.
  const seen = new Set<string>()
  const inline = (parts ?? []).filter(p => {
    if (!INLINE_PARTS[p.type]) {
      return false
    }
    // A placement without the Guide button (the Case history pane) has no way back from the
    // Guide to its chat: its answers do not link to it.
    if (p.type === 'guide' && !header.buttons.guide) {
      return false
    }
    // A hand-off back to the screen the placement sits in (the Case history's own "Open case
    // history") leads nowhere new: not shown there.
    if (
      p.type === 'handoff' &&
      p.route &&
      hidesLink?.(p.route, session.placementContext)
    ) {
      return false
    }
    const key = JSON.stringify([
      p.type,
      p.label,
      p.route,
      Object.entries(p.query ?? {}).sort(([a], [b]) => a.localeCompare(b)),
      p.docKey,
    ])
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  return (
    <>
      {blocks.map((part, index) => {
        const Renderer = BLOCK_PARTS[part.type]
        return (
          <Renderer
            key={part.actionId ?? `${part.type}-${index}`}
            part={part}
          />
        )
      })}
      {inline.length > 0 && (
        <MetaRow>
          {inline.map((part, index) => {
            const Renderer = INLINE_PARTS[part.type]
            return <Renderer key={`${part.type}-${index}`} part={part} />
          })}
        </MetaRow>
      )}
    </>
  )
}

export default observer(MessageParts)
